import {
  IconApps,
  IconCircleCheck,
  IconCircleX,
  IconCloudUp,
  IconDatabase,
  IconFolderFilled,
  IconMailForward,
  IconProgress,
  IconProps,
  IconRoute,
  IconWebhook
} from '@tabler/icons-react'
import { ReactElement } from 'react'
import {
  AlertSeverity,
  IntegrationType,
  MilestoneAlarmState,
  MilestoneStateKey,
  PATROL_RESERVED_TAGS,
  PatrolClipSeconds,
  PatrolInterval,
  PatrolReservedTag,
  PatrolVideoSource,
  PipelineType,
  VlmAction,
  VlmActionType
} from '@/types/vlm'
import { MilestonePipelineIcon } from './MilestonePipelineIcon'

export type PipelineTypeConfig = {
  icon: ReactElement<IconProps>
  labelId: string
  descriptionId: string
  comingSoon?: boolean
}

export const HTTP_PUSH_DEFAULT_PORT = 9090

export const MILESTONE_DEFAULT_TRIGGER_STATE = 'New'

export const MILESTONE_DEFAULT_PREROLL_SECONDS = 10
export const MILESTONE_DEFAULT_POSTROLL_SECONDS = 10

export const DEFAULT_ACTION_COOLDOWN_MINUTES = 15

// Matches the backend's camera limit per Milestone source.
export const MILESTONE_MAX_CAMERAS = 100

export const PATROL_DEFAULT_TIMES = ['08:00']
export const PATROL_DEFAULT_INTERVAL: PatrolInterval = '2h'
export const PATROL_DEFAULT_START_TIME = '08:00'
export const PATROL_DEFAULT_CLIP_SECONDS: PatrolClipSeconds = 20
export const PATROL_DEFAULT_ROUND_TIMEOUT_MINUTES = 15

export const MILESTONE_STATE_NAME_MAX_LENGTH = 100

export const MILESTONE_ACTION_STATE_KEYS: MilestoneStateKey[] = [
  'pending',
  'verified',
  'closed'
]

export type MilestoneStateConfig = {
  key: MilestoneStateKey
  icon: ReactElement<IconProps>
  labelId: string
  descriptionId: string
}

export const MILESTONE_STATE_CONFIG: MilestoneStateConfig[] = [
  {
    key: 'pending',
    icon: <IconProgress color="var(--mantine-color-blue-6)" />,
    labelId: 'vlm.pipelines.create.actions.milestone.state.pending.label',
    descriptionId:
      'vlm.pipelines.create.actions.milestone.state.pending.description'
  },
  {
    key: 'verified',
    icon: <IconCircleCheck color="var(--mantine-color-teal-6)" />,
    labelId: 'vlm.pipelines.create.actions.milestone.state.verified.label',
    descriptionId:
      'vlm.pipelines.create.actions.milestone.state.verified.description'
  },
  {
    key: 'closed',
    icon: <IconCircleX color="var(--mantine-color-red-6)" />,
    labelId: 'vlm.pipelines.create.actions.milestone.state.closed.label',
    descriptionId:
      'vlm.pipelines.create.actions.milestone.state.closed.description'
  }
]

export const MILESTONE_ACTION_DEFAULT_STATES: Record<
  MilestoneStateKey,
  MilestoneAlarmState
> = {
  pending: { name: 'Visionplatform pending', level: 2 },
  verified: { name: 'Visionplatform verified', level: 3 },
  closed: { name: 'Visionplatform closed', level: 12 }
}

// Editable scaffold seeded into the "Scene Context & Guidance" field for new
// pipelines. Bracketed prompts guide the user on what to fill in.
export const DEFAULT_USER_PROMPT = `The camera is located in [describe the location and environment, e.g. a retail plaza with outdoor trash containers].

Normal, everyday activity such as [what should be ignored, e.g. people walking, shopping, or using the bins correctly] is expected and should not be flagged.

Keep in mind that [edge cases that commonly cause false positives, e.g. throwing waste inside the container is normal].

Write all descriptions in English as [how output should be structured, e.g. one short sentence describing who did what and where].`

export const MAX_ACTION_RECIPIENTS = 5

// Max alert tags shown inline in the pipeline list before collapsing to "+N more"
export const MAX_VISIBLE_ALERT_TAGS = 3

// Mantine color name per severity, for Badge `color` prop
export const SEVERITY_BADGE_COLOR: Record<AlertSeverity, string> = {
  low: 'blue',
  medium: 'yellow',
  high: 'orange',
  critical: 'red'
}

// Highest → lowest, for ordering the most severe alerts first
export const SEVERITY_ORDER: AlertSeverity[] = [
  'critical',
  'high',
  'medium',
  'low'
]

export const buildHttpPushUrl = (port: number | string, endpointPath: string) =>
  `http://<device_ip>:${port}/push/${endpointPath.replace(/^\/+/, '')}`

export const PIPELINE_TYPES: PipelineType[] = [
  'visionplatform',
  'milestone',
  'virtual_patrol',
  'directory',
  'http_push'
]

export const SUPPORTED_VIDEO_FORMATS = ['.mp4', '.avi', '.mov']

export const PIPELINE_TYPE_CONFIG: Record<PipelineType, PipelineTypeConfig> = {
  directory: {
    icon: <IconFolderFilled color="var(--mantine-color-yellow-6)" />,
    labelId: 'vlm.pipelines.type.folder',
    descriptionId: 'vlm.pipelines.type.folder.description'
  },
  http_push: {
    icon: <IconWebhook color="var(--mantine-color-grape-6)" />,
    labelId: 'vlm.pipelines.type.httpPush',
    descriptionId: 'vlm.pipelines.type.httpPush.description'
  },
  visionplatform: {
    icon: <IconApps color="var(--mantine-color-teal-6)" />,
    labelId: 'vlm.pipelines.type.visionplatform',
    descriptionId: 'vlm.pipelines.type.visionplatform.description'
  },
  milestone: {
    icon: <MilestonePipelineIcon />,
    labelId: 'vlm.pipelines.type.milestone',
    descriptionId: 'vlm.pipelines.type.milestone.description'
  },
  virtual_patrol: {
    icon: <IconRoute color="var(--mantine-color-indigo-6)" />,
    labelId: 'vlm.pipelines.type.virtualPatrol',
    descriptionId: 'vlm.pipelines.type.virtualPatrol.description'
  }
}

export type IntegrationConfig = {
  icon: ReactElement<IconProps>
  labelId: string
  descriptionId: string
  alwaysEnabled?: boolean
  pipelineTypes?: PipelineType[]
}

export const INTEGRATION_CONFIG: Record<IntegrationType, IntegrationConfig> = {
  local_vlm_results: {
    icon: <IconDatabase color="var(--mantine-color-blue-6)" />,
    labelId: 'vlm.pipelines.integration.vlmDb.label',
    descriptionId: 'vlm.pipelines.integration.vlmDb.description',
    alwaysEnabled: true
  },
  // Opt-in via Pipeline.enable_cloud_results: when off, the backend accepts but
  // silently drops incoming events for this pipeline (device still sends them).
  cloud_vlm_results: {
    icon: <IconCloudUp color="var(--mantine-color-orange-6)" />,
    labelId: 'vlm.pipelines.integration.platformEvents.label',
    descriptionId: 'vlm.pipelines.integration.platformEvents.description'
  },
  // Distinct from cloud_vlm_results: this writes the VLM description back onto
  // the originating VisionPlatform application event, so it only exists where
  // there IS one. Bound to source.upload_descriptions.
  cloud_event_enrichment: {
    icon: <IconApps color="var(--mantine-color-teal-6)" />,
    labelId: 'vlm.pipelines.integration.appEventDescriptions.label',
    descriptionId: 'vlm.pipelines.integration.appEventDescriptions.description',
    pipelineTypes: ['visionplatform']
  }
}

export const getIntegrationsForType = (pipelineType: PipelineType) =>
  (Object.keys(INTEGRATION_CONFIG) as IntegrationType[]).filter((type) => {
    const { pipelineTypes } = INTEGRATION_CONFIG[type]
    return !pipelineTypes || pipelineTypes.includes(pipelineType)
  })

export type ActionTypeConfig = {
  icon: ReactElement<IconProps>
  labelId: string
}

export const ACTION_TYPES: VlmActionType[] = ['email', 'milestone']

export const ACTION_TYPE_CONFIG: Record<VlmActionType, ActionTypeConfig> = {
  email: {
    icon: <IconMailForward size={16} color="var(--mantine-color-yellow-8)" />,
    labelId: 'vlm.pipelines.create.actions.type.email'
  },
  milestone: {
    icon: <MilestonePipelineIcon size={16} />,
    labelId: 'vlm.pipelines.create.actions.type.milestone'
  }
}

export const PIPELINE_ACTION_TYPES: Record<PipelineType, VlmActionType[]> = {
  visionplatform: ['email', 'milestone'],
  directory: ['email', 'milestone'],
  http_push: ['email', 'milestone'],
  milestone: ['email', 'milestone'],
  virtual_patrol: ['email', 'milestone']
}

export const getActionTypesForPipeline = (
  pipelineType: PipelineType,
  videoSource?: PatrolVideoSource
): VlmActionType[] =>
  pipelineType === 'virtual_patrol' && videoSource === 'rtsp'
    ? ['email']
    : PIPELINE_ACTION_TYPES[pipelineType]

export const isPatrolReservedTag = (tag: string): tag is PatrolReservedTag =>
  (PATROL_RESERVED_TAGS as readonly string[]).includes(tag)

export type PatrolReservedTagConfig = {
  tag: PatrolReservedTag
  labelId: string
  tooltipId: string
  // Severity the pipeline manager gives the round summary.
  severity: AlertSeverity
}

export const PATROL_RESERVED_TAG_CONFIG: PatrolReservedTagConfig[] = [
  {
    tag: 'patrol_summary',
    labelId: 'vlm.pipelines.create.actions.tags.patrolSummary',
    tooltipId: 'vlm.pipelines.create.actions.tags.patrolSummary.tooltip',
    severity: 'low'
  },
  {
    tag: 'patrol_incomplete',
    labelId: 'vlm.pipelines.create.actions.tags.patrolIncomplete',
    tooltipId: 'vlm.pipelines.create.actions.tags.patrolIncomplete.tooltip',
    severity: 'high'
  }
]

// Delivers the round summary. Cooldown is per alert tag, so it's kept at the
// minimum: a longer one would drop summaries of rounds closer together.
export const createPatrolSummaryAction = (recipient?: string): VlmAction => ({
  type: 'email',
  alert_tags: [...PATROL_RESERVED_TAGS],
  recipients: recipient ? [recipient] : [],
  cooldown_minutes: 1
})

export const createDefaultAction = (type: VlmActionType): VlmAction => {
  if (type === 'milestone') {
    return {
      type,
      alert_tags: [],
      milestone_server_id: '',
      states: {
        pending: { ...MILESTONE_ACTION_DEFAULT_STATES.pending },
        verified: { ...MILESTONE_ACTION_DEFAULT_STATES.verified },
        closed: { ...MILESTONE_ACTION_DEFAULT_STATES.closed }
      }
    }
  }

  return {
    type,
    alert_tags: [],
    recipients: [],
    cooldown_minutes: DEFAULT_ACTION_COOLDOWN_MINUTES
  }
}
